// src/components/AGTUps/products/AGTCouncil-products/Sessions.js

import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Card, Chip, CircularProgress, 
  Divider, Tooltip, IconButton, useTheme, 
  LinearProgress, Badge, Button, Dialog, DialogActions, 
  DialogContent, DialogTitle, TextField, FormControl,
  InputLabel, Select, MenuItem, Snackbar, Alert,
  FormHelperText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  ChevronRight, Calendar, Clock, Users, 
  CalendarPlus, AlertCircle, CalendarCheck, X
} from 'lucide-react';
import consultService from '../../services/AGTUps-product-services/consultService';

const StyledCard = styled(Card)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(2),
    height: '100%',
    maxWidth: '550px',
    boxShadow: isWallpaperMode ? 'none' : '0 4px 20px rgba(0, 0, 0, 0.05)',
    border: `1px solid ${isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : 'rgba(0, 0, 0, 0.08)'}`,
    transition: 'all 0.3s ease',
    background: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : theme.palette.background.paper,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    margin: '0 auto',
  };
});

const SessionCard = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(2),
    border: `1px solid ${isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : 'rgba(0, 0, 0, 0.08)'}`,
    borderRadius: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    cursor: 'pointer',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.background.paper,
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: theme.shadows[2],
    },
  };
});

const TimeChip = styled(Chip)(({ theme, isStarted }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    borderRadius: theme.spacing(0.75),
    fontSize: '0.7rem',
    height: 24,
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : isStarted ? theme.palette.warning.light : theme.palette.primary.light,
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    color: isStarted ? theme.palette.warning.dark : theme.palette.primary.dark,
    fontWeight: 500,
  };
});

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

const DateBadge = styled(Badge)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    '& .MuiBadge-badge': {
      backgroundColor: isWallpaperMode ? '#FFFFFF' : theme.palette.primary.main,
      color: isWallpaperMode ? '#000000' : theme.palette.primary.contrastText,
      fontSize: '0.7rem',
      height: 20,
      minWidth: 20,
    },
  };
});

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
}));

// Styled buttons for wallpaper mode
const StyledPrimaryButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : '#0385FF',
    color: '#FFFFFF',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.4)' : '#0266cc'
    }
  };
});

const StyledOutlinedButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    borderColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    '&:hover': {
      borderColor: isWallpaperMode ? '#FFFFFF' : '#0266cc',
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(3, 133, 255, 0.08)'
    }
  };
});

const StyledIconButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    color: isWallpaperMode ? '#FFFFFF' : theme.palette.primary.main,
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(3, 133, 255, 0.08)'
    }
  };
});

// Función para calcular el tiempo restante
const getTimeRemaining = (scheduledTime) => {
  const now = new Date();
  const scheduled = new Date(scheduledTime);
  const diff = scheduled - now;
  
  if (diff <= 0) return { text: 'En curso', isStarted: true, percentage: 100 };
  
  const totalMinutes = 24 * 60;
  const remainingMinutes = Math.floor(diff / (1000 * 60));
  const percentage = Math.max(0, Math.min(100, 100 - (remainingMinutes / totalMinutes * 100)));
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  
  return { 
    text: `${hours}h ${minutes}m restantes`,
    isStarted: false,
    percentage
  };
};

const Sessions = ({ maxWidth = '550px' }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(new Date());
  const [scheduleTitle, setScheduleTitle] = useState('');
  const [scheduleExperts, setScheduleExperts] = useState([]);
  const [availableExperts, setAvailableExperts] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const theme = useTheme();

  useEffect(() => {
    const fetchSessions = async () => {
      setLoading(true);
      try {
        const res = await consultService.getUpcomingSessions();
        if (res.success && res.data) {
          // Ordenar por scheduledTime ascendente
          const sorted = res.data.sort((a, b) => new Date(a.scheduledTime) - new Date(b.scheduledTime));
          setSessions(sorted);
        }
      } catch (error) {
        console.error('Error fetching upcoming sessions:', error);
      } finally {
        setLoading(false);
      }
    };

    // Cargar expertos disponibles (simulación)
    setAvailableExperts([
      { id: 1, name: 'Estratega', role: 'Business Strategy', avatar: 'ES' },
      { id: 2, name: 'Analista', role: 'Market Analysis', avatar: 'AN' },
      { id: 3, name: 'Asesor', role: 'Financial Advisory', avatar: 'AS' },
      { id: 4, name: 'Innovador', role: 'Innovation Expert', avatar: 'IN' },
      { id: 5, name: 'Consultor', role: 'Business Consultant', avatar: 'CO' }
    ]);
    
    fetchSessions();
  }, []);

  // Función para formatear la fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Hoy';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Mañana';
    } else {
      return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    }
  };

  // Mostrar notificación
  const showNotification = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Abrir diálogo para programar nueva sesión
  const handleOpenScheduleDialog = () => {
    // Establecer fecha para mañana a las 12 PM por defecto
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(12, 0, 0, 0);
    
    setScheduleDate(tomorrow);
    setScheduleTitle('');
    setScheduleExperts([]);
    setDialogOpen(true);
  };

  // Programar nueva sesión
  const handleScheduleSession = async () => {
    if (!scheduleTitle.trim()) {
      showNotification('Por favor ingresa un título para la sesión', 'error');
      return;
    }
    
    if (scheduleExperts.length === 0) {
      showNotification('Selecciona al menos un experto para la sesión', 'error');
      return;
    }
    
    // Validar que la fecha sea futura
    if (scheduleDate < new Date()) {
      showNotification('La fecha de la sesión debe ser en el futuro', 'error');
      return;
    }
    
    try {
      // Simular ID para esta demo
      const consultationId = `session-${Date.now()}`;
      
      const sessionData = {
        consultationId,
        title: scheduleTitle,
        expertPanel: scheduleExperts,
        scheduledTime: scheduleDate.toISOString()
      };
      
      // Llamar al servicio para agendar
      const res = await consultService.scheduleMeeting(consultationId, scheduleDate.toISOString());
      
      if (res.success) {
        // Agregar la nueva sesión a la lista
        setSessions(prev => [...prev, { 
          ...sessionData, 
          status: 'programado',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }].sort((a, b) => new Date(a.scheduledTime) - new Date(b.scheduledTime)));
        
        showNotification('Sesión programada exitosamente', 'success');
        setDialogOpen(false);
      }
    } catch (error) {
      console.error('Error scheduling session:', error);
      showNotification('Error al programar la sesión', 'error');
    }
  };

  // Cancelar una sesión programada
  const handleCancelSession = async (sessionId) => {
    try {
      // En un entorno real, llamaríamos a un servicio para cancelar
      // await consultService.cancelSession(sessionId);
      
      // Eliminar la sesión de la lista
      setSessions(prev => prev.filter(session => session.consultationId !== sessionId));
      showNotification('Sesión cancelada correctamente', 'success');
    } catch (error) {
      console.error('Error cancelling session:', error);
      showNotification('Error al cancelar la sesión', 'error');
    }
  };

  return (
    <StyledCard sx={{ maxWidth }}>
      <HeaderBox>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Sesiones Programadas
        </Typography>
        <Box display="flex" alignItems="center">
          <StyledOutlinedButton
            startIcon={<CalendarPlus size={16} />}
            variant="outlined"
            size="small"
            onClick={handleOpenScheduleDialog}
            sx={{ mr: 1 }}
          >
            Nueva
          </StyledOutlinedButton>
          <Tooltip title="Ver todas">
            <StyledIconButton size="small">
              <ChevronRight size={20} />
            </StyledIconButton>
          </Tooltip>
        </Box>
      </HeaderBox>
      
      <Divider sx={{ mb: 3 }} />
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress size={28} />
        </Box>
      ) : sessions.length === 0 ? (
        <EmptyStateContainer>
          <Calendar size={40} color={theme.palette.text.disabled} />
          <Typography variant="body2" color="text.secondary" mt={1} mb={3}>
            No hay sesiones programadas.
          </Typography>
          <StyledOutlinedButton
            variant="outlined"
            startIcon={<CalendarPlus size={16} />}
            onClick={handleOpenScheduleDialog}
          >
            Programar sesión
          </StyledOutlinedButton>
        </EmptyStateContainer>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
            Recuerda que dispones de una consulta diaria con duración máxima de 1 hora.
          </Typography>
          
          {sessions.map((session, index) => {
            const timeInfo = getTimeRemaining(session.scheduledTime);
            const sessionDate = new Date(session.scheduledTime);
            
            return (
              <SessionCard key={index}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {session.title || 'Sesión programada'}
                    </Typography>
                    
                    <Box display="flex" alignItems="center" mt={0.5}>
                      <Clock size={14} style={{ marginRight: 6, color: theme.palette.text.secondary }} />
                      <Typography variant="caption" color="text.secondary">
                        {sessionDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                      <Box 
                        component="span" 
                        sx={{ 
                          width: 3, 
                          height: 3, 
                          borderRadius: '50%', 
                          bgcolor: 'text.disabled',
                          mx: 0.8
                        }} 
                      />
                      <DateBadge 
                        badgeContent={formatDate(session.scheduledTime) === 'Hoy' ? '!' : null}
                        color="primary"
                      >
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(session.scheduledTime)}
                        </Typography>
                      </DateBadge>
                    </Box>
                  </Box>
                  
                  <Box display="flex" alignItems="center">
                    <TimeChip
                      label={timeInfo.text}
                      isStarted={timeInfo.isStarted}
                      size="small"
                    />
                    <StyledIconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelSession(session.consultationId);
                      }}
                      sx={{ ml: 1 }}
                    >
                      <X size={16} />
                    </StyledIconButton>
                  </Box>
                </Box>
                
                <Box sx={{ mt: 1, mb: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={timeInfo.percentage}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      bgcolor: theme.palette.grey[100],
                      '& .MuiLinearProgress-bar': {
                        bgcolor: timeInfo.isStarted
                          ? theme.palette.warning.main
                          : (theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.primary.main),
                      }
                    }}
                  />
                </Box>
                
                {session.expertPanel && session.expertPanel.length > 0 && (
                  <Box display="flex" gap={1} mt={1}>
                    <Chip
                      size="small"
                      icon={<Users size={12} />}
                      label={`${session.expertPanel.length} expertos`}
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                    
                    <Chip
                      size="small"
                      icon={<Clock size={12} />}
                      label="60 minutos"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </Box>
                )}
              </SessionCard>
            );
          })}
        </>
      )}

      {/* Diálogo para programar nueva sesión */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <CalendarPlus size={20} style={{ marginRight: 8 }} />
            Programar nueva sesión
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            <TextField
              label="Título de la sesión"
              value={scheduleTitle}
              onChange={(e) => setScheduleTitle(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="Ej: Planificación estratégica, Análisis de mercado, etc."
            />
            
            <Box sx={{ mt: 3 }}>
              <TextField
                label="Fecha de la sesión"
                type="date"
                value={scheduleDate.toISOString().split('T')[0]}
                onChange={(e) => {
                  const newDate = new Date(scheduleDate);
                  const [year, month, day] = e.target.value.split('-');
                  newDate.setFullYear(parseInt(year));
                  newDate.setMonth(parseInt(month) - 1);
                  newDate.setDate(parseInt(day));
                  setScheduleDate(newDate);
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                margin="normal"
              />
              
              <TextField
                label="Hora de la sesión"
                type="time"
                value={`${scheduleDate.getHours().toString().padStart(2, '0')}:${scheduleDate.getMinutes().toString().padStart(2, '0')}`}
                onChange={(e) => {
                  const newDate = new Date(scheduleDate);
                  const [hours, minutes] = e.target.value.split(':');
                  newDate.setHours(parseInt(hours));
                  newDate.setMinutes(parseInt(minutes));
                  setScheduleDate(newDate);
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 900 }} // 15 min steps
                margin="normal"
              />
              
              <FormHelperText>
                Selecciona la fecha y hora para tu próxima sesión
              </FormHelperText>
            </Box>
            
            <FormControl fullWidth margin="normal">
              <InputLabel id="experts-select-label">Expertos</InputLabel>
              <Select
                labelId="experts-select-label"
                multiple
                value={scheduleExperts}
                onChange={(e) => setScheduleExperts(e.target.value)}
                label="Expertos"
              >
                {availableExperts.map((expert) => (
                  <MenuItem key={expert.id} value={expert}>
                    {expert.name} - {expert.role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <Box 
            sx={{ 
              mt: 2, 
              p: 2, 
              borderRadius: 1, 
              bgcolor: theme.palette.info.light, 
              color: theme.palette.info.dark,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1
            }}
          >
            <AlertCircle size={20} style={{ flexShrink: 0, marginTop: 2 }} />
            <Typography variant="body2">
              Dispones de una consulta diaria con duración máxima de 1 hora. Esta sesión reservará tu tiempo para que puedas consultar con los expertos seleccionados.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <StyledOutlinedButton
            onClick={() => setDialogOpen(false)}
            variant="outlined"
          >
            Cancelar
          </StyledOutlinedButton>
          <StyledPrimaryButton
            onClick={handleScheduleSession}
            variant="contained"
            color="primary"
            startIcon={<CalendarCheck size={16} />}
          >
            Programar sesión
          </StyledPrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </StyledCard>
  );
};

export default Sessions;